import { useEffect, useRef } from 'react';
import { toast } from '../ui/Toast';
import {
  DEPLOY_CHECK_INTERVAL_MS,
  DEPLOY_RELOAD_KEY,
  DEPLOY_TOAST_KEY,
  decideDeployAction,
  getSessionStorage,
  installStaleChunkWatch,
  matchesPath,
  readDeployedVersion,
  readFlag,
  writeFlag,
} from './deploy-watcher';

export const DEPLOY_TOAST_TITLE = 'A new version is available';

/* Focus, route change and the timer can land together; one request covers them. */
const MIN_CHECK_GAP_MS = 10_000;

export type DeployWatcherOptions = {
  /** The running bundle's version: `import.meta.env.VITE_APP_VERSION`. Unset (the dev server) turns the watcher off. */
  currentVersion: string | undefined;
  /** The router's current path. A change counts as a navigation. */
  pathname: string;
  /** Login, auth and public pages, reloaded as soon as a deploy is seen. See `matchesPath`. */
  reloadSafePaths: readonly string[];
  /** For tests; the apps rely on `currentVersion` being unset in dev. */
  enabled?: boolean;
  intervalMs?: number;
  versionUrl?: string;
  /** For tests. */
  reload?: () => void;
};

/**
 * Notices when a new build has been deployed under an open tab and brings the
 * tab onto it, without reloading over unsaved work:
 *
 * - checks `/version.json` on window focus, on every route change and every 5 minutes;
 * - on a login, auth or public page with nothing typed, reloads at once;
 * - anywhere else, never reloads on its own: the first time in a tab it says
 *   so in a toast whose Reload button the user presses when they choose. A
 *   navigation is not a reload: with deploys going out many times a day, every
 *   open tab would otherwise reload on its next click. One toast per tab,
 *   never a stack of them;
 * - a lazy chunk that fails to load (the old deploy's chunks are gone) raises
 *   the same toast; it never reloads on its own either (see lazyPage).
 *
 * Router-agnostic: pass the current path in, the hook treats a change as a navigation.
 * Needs a kit `ToastRegion` mounted.
 */
export function useDeployWatcher({
  currentVersion,
  pathname,
  reloadSafePaths,
  enabled = true,
  intervalMs = DEPLOY_CHECK_INTERVAL_MS,
  versionUrl = '/version.json',
  reload = () => window.location.reload(),
}: DeployWatcherOptions): void {
  /* Refs, so the listeners installed once always see the current values. */
  const latest = useRef({ pathname, reloadSafePaths, reload });
  latest.current = { pathname, reloadSafePaths, reload };

  /* The newest version seen while the reload waits for the user; the toast's button reloads for it. */
  const pending = useRef<string | null>(null);
  /* Raised on this page: a later deploy only moves `pending` on, it raises no second toast. */
  const toastRaised = useRef(false);
  /* Typed into a field since the last navigation. */
  const edited = useRef(false);
  const lastCheck = useRef(0);
  const checkRef = useRef<(force?: boolean) => void>(() => {});

  useEffect(() => {
    if (!enabled || !currentVersion) {
      return;
    }

    const storage = getSessionStorage();
    let cancelled = false;

    const reloadFor = (version: string) => {
      /* Recorded first: if the page comes back old, decideDeployAction stops there. */
      if (writeFlag(storage, DEPLOY_RELOAD_KEY, version)) {
        latest.current.reload();
      }
    };

    /* One toast on this page; its button reloads for the newest version seen. */
    const raiseToast = () => {
      toastRaised.current = true;
      toast.info(DEPLOY_TOAST_TITLE, {
        action: {
          label: 'Reload',
          onAction: () =>
            pending.current
              ? reloadFor(pending.current)
              : latest.current.reload(),
        },
        timeout: null,
      });
    };

    const check = async (force = false) => {
      const now = Date.now();
      if (!force && now - lastCheck.current < MIN_CHECK_GAP_MS) {
        return;
      }
      lastCheck.current = now;

      let deployed: string | null = null;
      try {
        const response = await fetch(versionUrl, { cache: 'no-store' });
        deployed = response.ok
          ? readDeployedVersion(await response.json())
          : null;
      } catch {
        /* Offline, or mid-deploy. The next check tries again. */
        return;
      }
      if (cancelled) {
        return;
      }

      const action = decideDeployAction({
        running: currentVersion,
        deployed,
        onReloadSafePage: matchesPath(
          latest.current.pathname,
          latest.current.reloadSafePaths,
        ),
        editing: edited.current || isEditingField(document.activeElement),
        reloadedFor: readFlag(storage, DEPLOY_RELOAD_KEY),
      });

      if (action === 'reload' && deployed) {
        reloadFor(deployed);
      } else if (action === 'defer' && deployed) {
        pending.current = deployed;
        /* Once per tab, kept in sessionStorage so a reload does not raise it again. */
        if (!toastRaised.current && !readFlag(storage, DEPLOY_TOAST_KEY)) {
          writeFlag(storage, DEPLOY_TOAST_KEY, deployed);
          raiseToast();
        }
      }
    };
    checkRef.current = force => {
      void check(force);
    };

    const onInput = () => {
      edited.current = true;
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        void check();
      }
    };
    const onFocus = () => void check();

    document.addEventListener('input', onInput, true);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);
    const timer = window.setInterval(() => void check(true), intervalMs);
    /* The page the user opened is already out of date: say so now, even if a
       toast was dismissed before a reload, and learn the version it is for. */
    const removeChunkWatch = installStaleChunkWatch({
      onStaleChunk: () => {
        const shown = toast
          .visible()
          .some(t => t.content.title === DEPLOY_TOAST_TITLE);
        if (!shown) {
          raiseToast();
        }
        void check(true);
      },
    });
    void check(true);

    return () => {
      cancelled = true;
      checkRef.current = () => {};
      document.removeEventListener('input', onInput, true);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
      window.clearInterval(timer);
      removeChunkWatch();
    };
  }, [enabled, currentVersion, intervalMs, versionUrl]);

  /* A navigation: the page that held any unsaved work is gone. It only checks
     again (a login or public page it lands on reloads there); the app page
     waits for the toast's button. */
  const previousPath = useRef(pathname);
  useEffect(() => {
    if (previousPath.current === pathname) {
      return;
    }
    previousPath.current = pathname;
    edited.current = false;

    checkRef.current();
  }, [pathname]);
}

/** A focused text field, select or rich-text editor: the user may be mid-entry. */
export function isEditingField(element: Element | null): boolean {
  if (!element) {
    return false;
  }
  if (element instanceof HTMLElement && element.isContentEditable) {
    return true;
  }
  if (
    element instanceof HTMLTextAreaElement ||
    element instanceof HTMLSelectElement
  ) {
    return true;
  }
  if (element instanceof HTMLInputElement) {
    return ![
      'button',
      'submit',
      'reset',
      'checkbox',
      'radio',
      'hidden',
    ].includes(element.type);
  }
  return false;
}
